import { preload } from "react-dom";
import { api, getAuthEpoch, heroSrcSet, HERO_SIZES, mediaUrl } from "./api";

const pending = new Map();
const timers = new Map();
const FRESH_MS = 30000;

const headerImages = (p) => {
  if (p.header_type === "slider") return [p.before_image, p.after_image];
  if (p.header_type === "image") return [p.hero_image];
  return [];
};

const preloadHeader = (p) => {
  headerImages(p).filter(Boolean).forEach((src) =>
    preload(mediaUrl(src), { as: "image", imageSrcSet: heroSrcSet(src), imageSizes: HERO_SIZES, fetchPriority: "high" }));
};

// A copy only counts if it is recent and was fetched for whoever is signed in now.
const fresh = (hit) => hit && Date.now() - hit.at < FRESH_MS && hit.epoch === getAuthEpoch();

const fetchProposal = (slug) => api.get(`/proposals/${slug}`).then(({ data }) => data);

// Starts loading a proposal (and, if asked, its header images) before the person opens it.
export function prefetchProposal(slug, { images = false } = {}) {
  if (!slug) return;
  const hit = pending.get(slug);
  if (fresh(hit)) {
    if (images && !hit.images) {
      hit.images = true;
      hit.promise.then(preloadHeader, () => {});
    }
    return;
  }
  const promise = fetchProposal(slug);
  promise.then((data) => images && preloadHeader(data), () => pending.delete(slug));
  pending.set(slug, { promise, images, at: Date.now(), epoch: getAuthEpoch() });
}

// Uses a prefetched copy once, then forgets it so likes and votes are always current next time.
export function loadProposal(slug) {
  const hit = pending.get(slug);
  if (!fresh(hit)) {
    pending.delete(slug);
    return fetchProposal(slug);
  }
  hit.promise.finally(() => {
    if (pending.get(slug) === hit) pending.delete(slug);
  });
  return hit.promise;
}

// Hover waits briefly so sweeping the pointer across a list doesn't fetch everything; a tap fetches the data only.
export const prefetchProps = (slug) => ({
  onMouseEnter: () => {
    clearTimeout(timers.get(slug));
    timers.set(slug, setTimeout(() => prefetchProposal(slug, { images: true }), 120));
  },
  onMouseLeave: () => clearTimeout(timers.get(slug)),
  onFocus: () => prefetchProposal(slug, { images: true }),
  onTouchStart: () => prefetchProposal(slug),
});
